import { AlertCircle } from 'lucide-react';
import { ReactNode } from 'react';

interface ConfirmChangeModalProps {
  from: string;
  to: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
  /** What is being changed, e.g. 'status' or 'category'. */
  label?: string;
}

const FLAG_DOT: Record<string, string> = {
  pending_yellow: 'bg-yellow-400',
  pending_red: 'bg-red-500',
};

export function ConfirmChangeModal({
  from,
  to,
  onConfirm,
  onCancel,
  isLoading = false,
  label = 'status',
}: ConfirmChangeModalProps) {
  const formatValue = (value: string): ReactNode => {
    if (FLAG_DOT[value]) {
      return (
        <span className="inline-flex items-center gap-1.5">
          Pending
          <span className={`inline-block size-2.5 rounded-xs ${FLAG_DOT[value]}`} />
        </span>
      );
    }
    return value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
  };

  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="shrink-0">
              <div className="flex size-12 items-center justify-center rounded-full bg-red-100">
                <AlertCircle className="size-6 text-red-600" />
              </div>
            </div>
            <div className="flex-1">
              <h2 className="mb-2 text-xl font-bold text-gray-900">
                Confirm {label.charAt(0).toUpperCase() + label.slice(1)} Change
              </h2>
              <p className="text-gray-600">
                Are you sure you want to change the {label} of this claim from{' '}
                <span className="font-semibold">{formatValue(from)}</span> to{' '}
                <span className="font-semibold">{formatValue(to)}</span>?
              </p>
            </div>
          </div>

          <div className="mt-6 flex gap-3">
            <button
              onClick={onCancel}
              disabled={isLoading}
              className="flex-1 rounded-md border border-gray-300 px-4 py-2 font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className="flex-1 rounded-md bg-red-600 px-4 py-2 font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLoading ? 'Changing...' : 'Confirm Change'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
